import * as Constants from '@/constants';
import utilityFunctions from '@/utils/functions';
import * as moment from 'moment';
import Swal from 'sweetalert2';

export default {
  name: 'MedicaidRepeatable',
  props: ['medicaidArray', 'student', 'index', 'record', 'showTooltip', 'v'],
  data() {
    return {
      startTimePickerVisible: false,
      endTimePickerVisible: false
    };
  },
  computed: {
    isMobile() {
      return this.$mq === 'mobile';
    },
    isNewRecord() {
      if (!this.student || !this.student.form || !this.student.form.createdDate) return true;
      // Primary: Use student.form.createdDate if available (from API)
      const deploymentDate = moment(Constants.PROCEDURE_START_TIME);
      const recordCreatedDate = moment(this.student.form.createdDate);
      return !recordCreatedDate.isBefore(deploymentDate);
    },
    minutesErrors() {
      const errors = [];
      const minutes = this.v.$each[this.index].minutes;
      if (!minutes.$dirty) return errors;
      if (!minutes.required) errors.push('Minutes are required.');
      if (!minutes.areMinutesGreaterThanZero) errors.push('Minutes must be greater than 0.');
      if (!minutes.isValidMinutes) errors.push('Invalid minutes.');
      return errors;
    },
    procedureErrors() {
      const errors = [];
      const procedure = this.v.$each[this.index].procedures;
      if (!procedure.$dirty) return errors;
      if (!procedure.required) errors.push('Procedure Description is required.');
      return errors;
    },
    selectedProcedure() {
      // Getter/Setter for v-select binding (single select for new records)
      // Returns first item from array, or empty object
      if (Array.isArray(this.record.procedures) && this.record.procedures.length > 0) {
        return this.record.procedures[0];
      }
      return null;
    },
    outcomeErrors() {
      const errors = [];
      const outcome = this.v.$each[this.index].outcome;
      if (!outcome.$dirty) return errors;
      if (!outcome.required) errors.push('Outcome is required.');
      return errors;
    },
    startTimeErrors() {
      const errors = [];
      if (!this.isNewRecord || !this.v.$each[this.index].startTime) return errors;
      const startTime = this.v.$each[this.index].startTime;
      if (!startTime.$dirty) return errors;
      if (!this.record.startTime) errors.push('Start Time is required.');
      if (!startTime.isValidTime) errors.push('Invalid time format.');
      if (!startTime.isProcedureStartTimeWithinSession) {
        errors.push('The Procedure Start Time and End Time must fall within the Session Start Time and End Time.');
      }
      return errors;
    },
    endTimeErrors() {
      const errors = [];
      if (!this.isNewRecord || !this.v.$each[this.index].endTime) return errors;
      const endTime = this.v.$each[this.index].endTime;
      if (!endTime.$dirty) return errors;
      if (!this.record.endTime) errors.push('End Time is required.');
      if (!endTime.isValidTime) errors.push('Invalid time format.');
      if (!endTime.isEndTimeEndGreaterThanStartTime) errors.push('End Time must be greater than Start Time');
      if (!endTime.isProcedureEndTimeWithinSession) {
        errors.push('The Procedure Start Time and End Time must fall within the Session Start Time and End Time.');
      }

      return errors;
    }
  },
  methods: {
    emitRemoveRecordClicked() {
      Swal.fire(
        {
          type: 'warning',
          text: 'Are you sure you want to delete this record?',
          showCancelButton: true
        })
        .then((response) => {
          if (response && response.value) this.$emit('delete:record', { index: this.index });
        });
    },
    emitAddRecordClicked() {
      this.$emit('add:record');
    },
    createProcedureObject(otherName) {
      return {
        categoryId: null,
        categoryName: 'Other',
        categorySortOrder: null,
        disabled: false,
        procedureFullName: `Other: ${otherName}`,
        procedureId: null,
        procedureName: otherName,
        procedureSortOrder: null,
        reimbursable: false
      };
    },
    formatProcedureArrayForSubmit(index) {
      // mark the field as touched for validations
      this.v.$each[index].procedures.$touch();
      // set the procedure array, create an object if procedure is other
      this.record.procedures = this.record.procedures.map((procedure) => {
        if (typeof procedure === 'string') {
          return this.createProcedureObject(procedure);
        }
        return {
          ...procedure
        };
      });
    },
    formatProcedureForNewRecord(index) {
      // For new records: ensure the selected item is properly formatted
      this.v.$each[index].procedures.$touch();
      // Ensure all procedures are objects (handle if user entered text)
      this.record.procedures = this.record.procedures.map((procedure) => {
        if (typeof procedure === 'string') {
          return this.createProcedureObject(procedure);
        }
        return {
          ...procedure
        };
      });
    },
    normalizeTimeInput(timeStr) {
      if (!timeStr) return null;
      const parsed = moment(timeStr, ['HH:mm', 'HH:mm:ss', 'h:mm A', 'h:mm a'], true);
      if (!parsed.isValid()) return null;
      return parsed.format('HH:mm');
    },
    startTimeChanged() {
      this.startTimePickerVisible = false;

      if (this.v.$each[this.index].startTime) {
        this.v.$each[this.index].startTime.$touch();
      }

      const normalizedStartTime = this.normalizeTimeInput(this.record.startTime);
      if (normalizedStartTime) {
        this.record.startTime = normalizedStartTime;
      }

      const startTime = this.record.startTime;
      const endTime = this.record.endTime;
      const elapsedTime = this.record.minutes;

      if (startTime && endTime) {
        this.record.minutes = utilityFunctions.calculateServiceLength(startTime, endTime);
        this.v.$each[this.index].minutes.$touch();
      } else if (startTime && elapsedTime) {
        const startTimeValidation = this.v.$each[this.index].startTime;
        // Start Time must be validated against the Session window before deriving an End
        // Time from it - otherwise an out-of-session Start Time silently produces an
        // equally-invalid End Time with no error shown.
        if (startTimeValidation && !startTimeValidation.isProcedureStartTimeWithinSession) {
          return;
        }
        this.record.endTime = utilityFunctions.calculateEndTime(startTime, elapsedTime);
        this.v.$each[this.index].endTime.$touch();
      }
    },
    endTimeChanged() {
      this.endTimePickerVisible = false;

      if (this.v.$each[this.index].endTime) {
        this.v.$each[this.index].endTime.$touch();
      }

      const normalizedEndTime = this.normalizeTimeInput(this.record.endTime);
      if (normalizedEndTime) {
        this.record.endTime = normalizedEndTime;
      }

      const startTime = this.record.startTime;
      const endTime = this.record.endTime;
      const elapsedTime = this.record.minutes;

      if (startTime && endTime) {
        this.record.minutes = utilityFunctions.calculateServiceLength(startTime, endTime);
        this.v.$each[this.index].minutes.$touch();
      } else if (endTime && elapsedTime) {
        const endTimeValidation = this.v.$each[this.index].endTime;
        // Symmetric to startTimeChanged() - validate End Time against the Session window
        // before deriving a Start Time from it.
        if (endTimeValidation && !endTimeValidation.isProcedureEndTimeWithinSession) {
          return;
        }
        this.record.startTime = utilityFunctions.calculateStartTime(endTime, elapsedTime);
        this.v.$each[this.index].startTime.$touch();
      }
    },
    remove(item) {
      this.record.procedures = this.record.procedures.filter((proc) => {
        return proc.procedureName !== item;
      });
    }
  },
  watch: {
    // calculate total minutes is minutes changes
    'record.minutes': function recordMinutesWatcher() {
      const serviceLength = this.record.minutes;
      const startTime = this.record.startTime;
      if (startTime && serviceLength) {
        this.record.endTime = utilityFunctions.calculateEndTime(startTime, serviceLength);
        this.v.$each[this.index].endTime.$touch();
      }

      this.$emit('calculate:minutes');
    },
    // calculate total minutes if procedures change
    'record.procedures': function recordProceduresWatcher() {
      this.$emit('calculate:minutes');
    }
  }
};



// WEBPACK FOOTER //
// ./src/components/service-log/form/medicaid-repeatable/medicaid-repeatable.js